import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { VehicleCard } from "@/components/VehicleCard";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Combobox } from "@/components/ui/combobox";
import { Search, SlidersHorizontal, PackageX } from "lucide-react";

const CATS = [
  { v: "motor_listrik", l: "Motor Listrik" },
  { v: "sepeda_listrik", l: "Sepeda Listrik" },
  { v: "motor_bensin", l: "Motor Bensin" },
];

export default function Catalog() {
  const [category, setCategory] = useState("motor_listrik");
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [scheme, setScheme] = useState("all");
  const [sort, setSort] = useState("completeness_desc");
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/vehicles/brands", { params: { category } }).then((r) => setBrands(r.data.brands)).catch(() => {});
    setBrand("all");
  }, [category]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { category, sort };
    if (q) params.q = q;
    if (brand !== "all") params.brand = brand;
    if (scheme !== "all") params.battery_scheme = scheme;
    api.get("/vehicles", { params }).then((r) => setItems(r.data.items)).catch(() => {}).finally(() => setLoading(false));
  }, [category, q, brand, scheme, sort]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const isElectric = category !== "motor_bensin";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="catalog-page">
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy mb-2">Katalog Kendaraan</h1>
      <p className="text-muted-foreground mb-6">Data kendaraan berasal dari <span className="font-semibold text-teal">sumber resmi & daftar pemerintah</span>, diverifikasi bertahap. Semua angka merupakan estimasi.</p>

      <Tabs value={category} onValueChange={setCategory} className="mb-6">
        <TabsList className="rounded-full bg-secondary p-1 h-auto flex-wrap" data-testid="category-tabs">
          {CATS.map((c) => <TabsTrigger key={c.v} value={c.v} data-testid={`tab-${c.v}`} className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-teal">{c.l}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="bg-white rounded-2xl border border-border p-4 mb-8 grid md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari merek atau model…" className="rounded-xl pl-9 h-11" />
        </div>
        <Combobox testId="catalog-brand" value={brand} onChange={setBrand}
          placeholder="Semua Merek" searchPlaceholder="Cari merek…"
          options={[{ value: "all", label: "Semua Merek" }, ...brands.map((b) => ({ value: b, label: b }))]} />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="catalog-sort" className="rounded-xl h-11"><SlidersHorizontal className="w-4 h-4 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="completeness_desc">Paling Lengkap</SelectItem>
            <SelectItem value="price_asc">Harga Termurah</SelectItem>
            <SelectItem value="price_desc">Harga Tertinggi</SelectItem>
            {isElectric && <SelectItem value="range_desc">Jarak Terjauh</SelectItem>}
            {isElectric && <SelectItem value="power_desc">Tenaga Terbesar</SelectItem>}
          </SelectContent>
        </Select>
        {isElectric && (
          <Select value={scheme} onValueChange={setScheme}>
            <SelectTrigger data-testid="catalog-scheme" className="rounded-xl h-11"><SelectValue placeholder="Skema Baterai" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Semua Skema Baterai</SelectItem>
              <SelectItem value="beli">Beli</SelectItem>
              <SelectItem value="sewa">Sewa</SelectItem>
              <SelectItem value="swap">Swap</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20" data-testid="catalog-empty">
          <PackageX className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
          <p className="text-navy font-semibold">Tidak ada kendaraan yang cocok</p>
          <p className="text-muted-foreground text-sm">Coba ubah kata kunci atau filter Anda.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="catalog-grid">
          {items.map((v) => <VehicleCard key={v.vehicle_id} v={v} />)}
        </div>
      )}
    </div>
  );
}
